import { useCallback } from "react";

import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectIsVirtualUserPresentInClass,
  selectParticipantBarVisibleClassUserIds,
  selectPinnedClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import { useAvPubsubMessaging } from "utils/av";

export const useFeatureParticipantTools = (classUserId = "") => {
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { canAuthorSection } = useCapabilities();
  const pinnedClassUserIds = useAppSelector(selectPinnedClassUserIds);
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const participantBarVisibleClassUserIds = useAppSelector(selectParticipantBarVisibleClassUserIds);

  const onFeatureClassUserId = useCallback(() => {
    if (!classUserId || !canAuthorSection) return;

    if (pinnedClassUserIds.includes(classUserId)) {
      return;
    }

    sendPubsubVirtualClassStateMessage({
      action: "pin-class-users",
      class_user_ids: [classUserId],
    });
  }, [classUserId, pinnedClassUserIds, canAuthorSection, sendPubsubVirtualClassStateMessage]);

  const onFeatureClassUserIdList = useCallback(
    (newClassUserIds: string[]) => {
      if (!canAuthorSection || newClassUserIds.length === 0) return;

      sendPubsubVirtualClassStateMessage({
        action: "pin-class-users",
        class_user_ids: newClassUserIds,
      });
    },
    [canAuthorSection, sendPubsubVirtualClassStateMessage],
  );

  const isParticipantVisibleInBar =
    classUserId != null && participantBarVisibleClassUserIds.includes(classUserId);

  const featureParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.view_participant_not_available")
    : isParticipantVisibleInBar
      ? t("user_options_menu.view_participant_already_visible")
      : undefined;

  return { onFeatureClassUserId, onFeatureClassUserIdList, featureParticipantDisabledText };
};
